import { useState } from "react";
import { STATUSES, type Project, type Status, type Task } from "../types";
import { TaskCard } from "./TaskCard";

interface Props {
  tasks: Task[];
  project: Project;
  readOnly: boolean;
  onOpen: (task: Task) => void;
  onMove: (task: Task, status: Status) => void;
}

const DONE_VISIBLE = 3;

export function Board({ tasks, project, readOnly, onOpen, onMove }: Props) {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<Status | null>(null);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="board">
      {STATUSES.map((status) => {
        const all = tasks.filter((t) => t.status === status);
        const sorted = [...all].sort((a, b) =>
          status === "Done"
            ? (b.deadline || "").localeCompare(a.deadline || "")
            : (a.deadline || "9999-99-99").localeCompare(b.deadline || "9999-99-99")
        );
        const collapsed = status === "Done" && !expanded && sorted.length > DONE_VISIBLE;
        const shown = collapsed ? sorted.slice(0, DONE_VISIBLE) : sorted;
        const hidden = sorted.length - shown.length;

        return (
          <section className="col" key={status}>
            <h2 className="col__head">
              {status}
              <span className="col__count">{all.length}</span>
            </h2>
            <div
              className={`col__body${over === status ? " col__body--over" : ""}`}
              onDragOver={(e) => {
                if (readOnly) return;
                e.preventDefault();
                setOver(status);
              }}
              onDragLeave={() => setOver((s) => (s === status ? null : s))}
              onDrop={(e) => {
                e.preventDefault();
                setOver(null);
                if (readOnly) return;
                const id = e.dataTransfer.getData("text/plain");
                const task = tasks.find((t) => t.id === id);
                if (task && task.status !== status) onMove(task, status);
              }}
            >
              {shown.length === 0 && <p className="col__empty">Nothing here.</p>}
              {shown.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  project={project}
                  draggable={!readOnly}
                  dragging={dragging === task.id}
                  onOpen={onOpen}
                  onDragStart={(t) => setDragging(t.id)}
                  onDragEnd={() => setDragging(null)}
                />
              ))}
              {collapsed && hidden > 0 && (
                <button className="col__more" onClick={() => setExpanded(true)}>
                  {hidden} older
                </button>
              )}
              {status === "Done" && expanded && sorted.length > DONE_VISIBLE && (
                <button className="col__more" onClick={() => setExpanded(false)}>
                  Show less
                </button>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
